import { useState, useRef, useCallback, useEffect } from 'react'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'

export type PlacesLoadingState = 'idle' | 'loading' | 'ready' | 'error'

export type PlacePredictionItem = {
  placeId: string
  mainText: string
  secondaryText: string
  fullText: string
  _prediction: google.maps.places.PlacePrediction
}

export type ResolvedPlace = {
  coords: { lat: number; lng: number }
  suburb: string
  state: string
  country: string
  formattedDescription: string
  formattedAddress: string
}

export type UsePlacesAutocompleteOptions = {
  apiKey: string
  debounceMs?: number
  minChars?: number
  maxResults?: number
  countryRestriction?: string
  includedPrimaryTypes?: string[]
}

export type UsePlacesAutocompleteReturn = {
  apiState: PlacesLoadingState
  predictions: PlacePredictionItem[]
  isLoadingPredictions: boolean
  isResolvingPlace: boolean
  predictionError: string | null
  resolutionError: string | null
  setInput: (value: string) => void
  selectPrediction: (prediction: PlacePredictionItem) => Promise<ResolvedPlace>
  clearPredictions: () => void
  reset: () => void
}

const API_LOAD_TIMEOUT = 10_000

const extractAddressComponent = (
  components: google.maps.places.AddressComponent[],
  type: string
): string => {
  const component = components.find(c => c.types.includes(type))
  return component?.longText ?? ''
}

const extractSuburb = (
  components: google.maps.places.AddressComponent[]
): string => {
  return (
    extractAddressComponent(components, 'locality') ||
    extractAddressComponent(components, 'sublocality') ||
    extractAddressComponent(components, 'postal_town') ||
    extractAddressComponent(components, 'administrative_area_level_3') ||
    ''
  )
}

const extractState = (
  components: google.maps.places.AddressComponent[]
): string => {
  return extractAddressComponent(components, 'administrative_area_level_1')
}

const extractCountry = (
  components: google.maps.places.AddressComponent[]
): string => {
  return extractAddressComponent(components, 'country')
}

export const usePlacesAutocomplete = (
  options: UsePlacesAutocompleteOptions
): UsePlacesAutocompleteReturn => {
  const {
    apiKey,
    debounceMs = 300,
    minChars = 3,
    maxResults = 5,
    countryRestriction,
    includedPrimaryTypes = [
      'locality',
      'sublocality',
      'postal_code',
      'administrative_area_level_3'
    ]
  } = options

  const [apiState, setApiState] = useState<PlacesLoadingState>('idle')
  const [predictions, setPredictions] = useState<PlacePredictionItem[]>([])
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false)
  const [isResolvingPlace, setIsResolvingPlace] = useState(false)
  const [predictionError, setPredictionError] = useState<string | null>(null)
  const [resolutionError, setResolutionError] = useState<string | null>(null)

  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  // Load the Google Maps Places API
  useEffect(() => {
    mountedRef.current = true
    setApiState('loading')

    setOptions({ key: apiKey })

    let timedOut = false

    loadTimeoutRef.current = setTimeout(() => {
      timedOut = true
      if (mountedRef.current) {
        setApiState('error')
      }
    }, API_LOAD_TIMEOUT)

    importLibrary('places').then(
      () => {
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current)
        }
        if (timedOut || !mountedRef.current) return
        sessionTokenRef.current =
          new google.maps.places.AutocompleteSessionToken()
        setApiState('ready')
      },
      () => {
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current)
        }
        if (!mountedRef.current) return
        setApiState('error')
      }
    )

    return () => {
      mountedRef.current = false
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current)
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [apiKey])

  const fetchPredictions = useCallback(
    async (input: string) => {
      if (!mountedRef.current) return

      setIsLoadingPredictions(true)
      setPredictionError(null)

      try {
        const request: google.maps.places.AutocompleteRequest = {
          input,
          sessionToken: sessionTokenRef.current!,
          includedPrimaryTypes
        }

        if (countryRestriction) {
          request.includedRegionCodes = [countryRestriction]
        }

        const { suggestions } =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request
          )

        if (!mountedRef.current) return

        const placeSuggestions = suggestions
          .filter(
            (
              s
            ): s is google.maps.places.AutocompleteSuggestion & {
              placePrediction: google.maps.places.PlacePrediction
            } => s.placePrediction != null
          )
          .slice(0, maxResults)

        const items: PlacePredictionItem[] = placeSuggestions.map(s => {
          const prediction = s.placePrediction
          return {
            placeId: prediction.placeId ?? '',
            mainText: prediction.mainText?.text ?? '',
            secondaryText: prediction.secondaryText?.text ?? '',
            fullText: prediction.text?.text ?? '',
            _prediction: prediction
          }
        })

        setPredictions(items)
      } catch {
        if (!mountedRef.current) return
        setPredictionError('Suggestions temporarily unavailable')
        setPredictions([])
      } finally {
        if (mountedRef.current) {
          setIsLoadingPredictions(false)
        }
      }
    },
    [countryRestriction, includedPrimaryTypes, maxResults]
  )

  const setInput = useCallback(
    (value: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      setPredictionError(null)

      if (value.length < minChars) {
        setPredictions([])
        setIsLoadingPredictions(false)
        return
      }

      if (apiState !== 'ready') return

      debounceTimerRef.current = setTimeout(() => {
        fetchPredictions(value)
      }, debounceMs)
    },
    [apiState, debounceMs, fetchPredictions, minChars]
  )

  const selectPrediction = useCallback(
    async (prediction: PlacePredictionItem): Promise<ResolvedPlace> => {
      setIsResolvingPlace(true)
      setResolutionError(null)

      try {
        const place = prediction._prediction.toPlace()
        await place.fetchFields({
          fields: ['location', 'addressComponents', 'formattedAddress']
        })

        const location = place.location
        if (!location) {
          throw new Error('No location data returned')
        }

        const components = place.addressComponents ?? []
        const suburb = extractSuburb(components)
        const state = extractState(components)
        const country = extractCountry(components)

        const parts = [suburb, state, country].filter(Boolean)
        const formattedDescription = parts.join(', ')
        const formattedAddress = place.formattedAddress ?? ''

        // Create a new session token after successful resolution
        sessionTokenRef.current =
          new google.maps.places.AutocompleteSessionToken()

        const resolved: ResolvedPlace = {
          coords: { lat: location.lat(), lng: location.lng() },
          suburb,
          state,
          country,
          formattedDescription,
          formattedAddress
        }

        setPredictions([])

        return resolved
      } catch {
        setResolutionError('Could not resolve location details')
        throw new Error('Could not resolve location details')
      } finally {
        if (mountedRef.current) {
          setIsResolvingPlace(false)
        }
      }
    },
    []
  )

  const clearPredictions = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    setPredictions([])
    setPredictionError(null)
    setIsLoadingPredictions(false)
  }, [])

  const reset = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    setPredictions([])
    setPredictionError(null)
    setResolutionError(null)
    setIsLoadingPredictions(false)
    setIsResolvingPlace(false)
    if (apiState === 'ready') {
      sessionTokenRef.current =
        new google.maps.places.AutocompleteSessionToken()
    }
  }, [apiState])

  return {
    apiState,
    predictions,
    isLoadingPredictions,
    isResolvingPlace,
    predictionError,
    resolutionError,
    setInput,
    selectPrediction,
    clearPredictions,
    reset
  }
}
